import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../services/api";

const BASE =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "http://107.172.39.143";

/* Safely resolve image URLs — Cloudinary URLs are already absolute */
const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;   // Cloudinary or any absolute URL
  return `${BASE}${url}`;                   // legacy relative /uploads/... path
};

const EMPTY = { title: "", summary: "", body: "", isPublished: true };

/* ─── tiny reusable UI pieces defined OUTSIDE the parent so they never lose focus ─── */

function Label({ text, required }) {
  return (
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
      {text}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
  );
}

function ErrMsg({ msg }) {
  return msg ? <p className="text-red-500 text-xs mt-1">{msg}</p> : null;
}

function TInput({ value, onChange, placeholder, err }) {
  return (
    <>
      <input
        type="text" value={value} placeholder={placeholder}
        onChange={e => onChange(e.target.value)}
        className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition
          focus:ring-2 ${err ? "border-red-400 focus:ring-red-200" : "border-gray-200 focus:ring-green-300"}`}
      />
      <ErrMsg msg={err} />
    </>
  );
}

function TArea({ value, onChange, placeholder, rows = 4, err }) {
  return (
    <>
      <textarea
        value={value} placeholder={placeholder} rows={rows}
        onChange={e => onChange(e.target.value)}
        className={`w-full rounded-xl border px-4 py-2.5 text-sm outline-none transition resize-y
          focus:ring-2 ${err ? "border-red-400 focus:ring-red-200" : "border-gray-200 focus:ring-green-300"}`}
      />
      <ErrMsg msg={err} />
    </>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className="flex items-center gap-3 group">
      <div className={`relative w-11 h-6 rounded-full transition-colors ${value ? "bg-green-500" : "bg-gray-300"}`}>
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? "translate-x-5" : ""}`} />
      </div>
      <span className="text-sm text-gray-600">{label}</span>
    </button>
  );
}

/* ─── Flip preview card (admin-side) ─── */
function PreviewCard({ post, onEdit, onDelete, deleting }) {
  const [flipped, setFlipped] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const imgSrc = resolveImg(post.imageUrl);

  return (
    <>
      {lightbox && imgSrc && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(false)}
        >
          <img src={imgSrc} alt={post.title}
            className="max-w-full max-h-[88vh] rounded-2xl shadow-2xl object-contain"
            onClick={e => e.stopPropagation()} />
          <button onClick={() => setLightbox(false)}
            className="absolute top-5 right-5 text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 text-xl flex items-center justify-center">
            ✕
          </button>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {/* Card */}
        <div className="relative" style={{ perspective: 900, height: 340 }}>
          <div style={{
            position: "relative", width: "100%", height: "100%",
            transformStyle: "preserve-3d",
            transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            transition: "transform 0.5s ease",
          }}>
            {/* FRONT */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden border border-gray-100 shadow bg-white flex flex-col"
              style={{ backfaceVisibility: "hidden" }}>
              {imgSrc ? (
                <div className="relative h-40 shrink-0 overflow-hidden cursor-zoom-in group"
                  onClick={() => setLightbox(true)}>
                  <img src={imgSrc} alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition flex items-center justify-center">
                    <span className="text-white text-3xl opacity-0 group-hover:opacity-100 transition drop-shadow">🔍</span>
                  </div>
                  {!post.isPublished && (
                    <span className="absolute top-2 right-2 text-xs bg-yellow-400 text-yellow-900 font-bold px-2 py-0.5 rounded-full">
                      DRAFT
                    </span>
                  )}
                </div>
              ) : (
                <div className="h-40 shrink-0 bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center relative">
                  <span className="text-5xl">🌿</span>
                  {!post.isPublished && (
                    <span className="absolute top-2 right-2 text-xs bg-yellow-400 text-yellow-900 font-bold px-2 py-0.5 rounded-full">
                      DRAFT
                    </span>
                  )}
                </div>
              )}
              <div className="flex-1 p-4 flex flex-col min-h-0">
                <h3 className="font-bold text-gray-800 text-sm leading-tight line-clamp-2 mb-2">{post.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed flex-1 line-clamp-3">{post.summary}</p>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50">
                  <span className="text-xs text-gray-300">
                    {new Date(post.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                  <button onClick={() => setFlipped(true)}
                    className="text-xs font-semibold text-green-600 hover:text-green-800 transition bg-green-50 hover:bg-green-100 px-3 py-1 rounded-full">
                    Read more →
                  </button>
                </div>
              </div>
            </div>

            {/* BACK */}
            <div className="absolute inset-0 rounded-2xl overflow-hidden shadow p-5 flex flex-col"
              style={{
                backfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                background: "linear-gradient(145deg, #2D5A3D, #4A7C59)",
              }}>
              <div className="flex items-start justify-between mb-3 gap-2">
                <h3 className="text-white font-bold text-sm leading-tight flex-1">{post.title}</h3>
                <button onClick={() => setFlipped(false)}
                  className="shrink-0 text-white/60 hover:text-white text-lg leading-none w-7 h-7 flex items-center justify-center bg-white/10 rounded-full transition">
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.25) transparent" }}>
                <p className="text-green-100 text-xs leading-7 whitespace-pre-line">{post.body}</p>
              </div>
              <p className="text-green-300/60 text-xs mt-3 pt-3 border-t border-white/10">— {post.postedBy}</p>
            </div>
          </div>
        </div>

        {/* Action buttons below card */}
        <div className="flex gap-2">
          <button onClick={() => onEdit(post)}
            className="flex-1 py-2 text-xs font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition">
            ✏️ Edit
          </button>
          <button onClick={() => onDelete(post._id)} disabled={deleting === post._id}
            className="flex-1 py-2 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 rounded-xl transition disabled:opacity-40">
            {deleting === post._id ? "Deleting…" : "🗑️ Delete"}
          </button>
        </div>
      </div>
    </>
  );
}

/* ─── Main admin page ─── */
export default function AdminDailyPosts() {
  const [posts,      setPosts]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [form,       setForm]       = useState(EMPTY);
  const [editingId,  setEditingId]  = useState(null);
  const [errors,     setErrors]     = useState({});
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(null);
  const [msg,        setMsg]        = useState({ text: "", ok: true });
  const [imgFile,    setImgFile]    = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [removeImg,  setRemoveImg]  = useState(false);
  const fileRef = useRef();
  const formRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/daily-posts/all");
      setPosts(r.data || []);
    } catch {
      try {
        const r = await api.get("/daily-posts");
        setPosts(r.data || []);
      } catch { /* silent */ }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: "" }));
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImgFile(file);
    setRemoveImg(false);
    const reader = new FileReader();
    reader.onloadend = () => setImgPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearImg = () => {
    setImgFile(null);
    setImgPreview("");
    setRemoveImg(!!editingId); // only flag server removal when editing
    if (fileRef.current) fileRef.current.value = "";
  };

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setErrors({});
    setMsg({ text: "", ok: true });
    setImgFile(null);
    setImgPreview("");
    setRemoveImg(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openEdit = (post) => {
    setEditingId(post._id);
    setForm({ title: post.title, summary: post.summary, body: post.body, isPublished: post.isPublished });
    setImgFile(null);
    setImgPreview(resolveImg(post.imageUrl) || "");
    setRemoveImg(false);
    setErrors({});
    setMsg({ text: "", ok: true });
    if (fileRef.current) fileRef.current.value = "";
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim())   e.title   = "Title is required";
    if (!form.summary.trim()) e.summary = "Summary (card front text) is required";
    if (!form.body.trim())    e.body    = "Full content (card back text) is required";
    return e;
  };

  const save = async () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    setMsg({ text: "", ok: true });
    try {
      const fd = new FormData();
      fd.append("title",       form.title.trim());
      fd.append("summary",     form.summary.trim());
      fd.append("body",        form.body.trim());
      fd.append("isPublished", form.isPublished ? "true" : "false");
      if (imgFile)       fd.append("image", imgFile);
      if (removeImg)     fd.append("removeImage", "true");

      if (editingId) {
        await api.put(`/daily-posts/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMsg({ text: "✅ Post updated successfully!", ok: true });
      } else {
        await api.post("/daily-posts", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setMsg({ text: "✅ Post published!", ok: true });
      }
      resetForm();
      load();
    } catch (err) {
      setMsg({ text: "❌ " + (err.response?.data?.error || "Save failed. Please try again."), ok: false });
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Permanently delete this post and its image from the database?")) return;
    setDeleting(id);
    try {
      await api.delete(`/daily-posts/${id}`);
      setPosts(p => p.filter(x => x._id !== id));
      if (editingId === id) resetForm();
    } catch (err) {
      alert("Delete failed: " + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">📰 Daily Posts</h1>
        <p className="text-sm text-gray-400 mt-1">
          Post health tips, Ayurvedic insights or clinic updates. They appear as flip cards on the home page. All admins can post.
        </p>
      </div>

      {/* ── Form ── */}
      <div ref={formRef} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        <h2 className="text-base font-bold text-gray-700 pb-3 border-b border-gray-100">
          {editingId ? "✏️ Editing Post" : "➕ New Post"}
        </h2>

        {msg.text && (
          <div className={`text-sm font-medium px-4 py-2.5 rounded-xl ${msg.ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
            {msg.text}
          </div>
        )}

        <div>
          <Label text="Post Title" required />
          <TInput value={form.title} onChange={v => setF("title", v)}
            placeholder="e.g. 5 Benefits of Triphala for Digestion" err={errors.title} />
        </div>

        <div>
          <Label text="Card Front — Short Summary" required />
          <p className="text-xs text-gray-400 mb-1.5">Shown on the front face of the card (2–3 sentences max)</p>
          <TArea value={form.summary} onChange={v => setF("summary", v)} rows={3}
            placeholder="A quick teaser that makes readers want to flip the card…" err={errors.summary} />
        </div>

        <div>
          <Label text="Card Back — Full Content" required />
          <p className="text-xs text-gray-400 mb-1.5">Shown when the card is flipped. Can be longer — it scrolls.</p>
          <TArea value={form.body} onChange={v => setF("body", v)} rows={7}
            placeholder="The complete article, health tip or announcement…" err={errors.body} />
        </div>

        {/* Image upload */}
        <div>
          <Label text="Post Image (optional)" />
          {imgPreview && !removeImg ? (
            <div className="relative inline-block mb-3 group">
              <img src={imgPreview} alt="preview"
                className="h-44 rounded-xl object-cover border border-gray-200 shadow-sm" />
              <button type="button" onClick={clearImg}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs shadow flex items-center justify-center hover:bg-red-600 transition">
                ✕
              </button>
            </div>
          ) : null}
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile}
            className="block text-sm text-gray-500
              file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0
              file:text-sm file:font-semibold file:bg-green-50 file:text-green-700
              hover:file:bg-green-100 transition" />
          <p className="text-xs text-gray-400 mt-1.5">Max 5 MB · JPG, PNG, GIF or WebP</p>
        </div>

        {/* Published toggle */}
        <Toggle value={form.isPublished} onChange={v => setF("isPublished", v)}
          label={form.isPublished ? "Published — visible on website" : "Draft — hidden from website"} />

        {/* Buttons */}
        <div className="flex gap-3 pt-1">
          {editingId && (
            <button type="button" onClick={resetForm}
              className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-500 hover:bg-gray-50 transition">
              Cancel
            </button>
          )}
          <button type="button" onClick={save} disabled={saving}
            className="px-7 py-2.5 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 disabled:opacity-60 transition shadow-sm">
            {saving ? "Saving…" : editingId ? "Update Post" : "Publish Post"}
          </button>
        </div>
      </div>

      {/* ── All posts grid ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h2 className="text-base font-bold text-gray-700 pb-3 border-b border-gray-100 mb-6">
          📋 All Posts
          <span className="ml-2 text-xs font-normal text-gray-400">({posts.length})</span>
        </h2>

        {loading ? (
          <div className="text-center py-12 text-gray-400 text-sm">Loading posts…</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12 text-gray-400 text-sm">
            No posts yet. Use the form above to create your first post.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(post => (
              <PreviewCard
                key={post._id} post={post}
                onEdit={openEdit} onDelete={deletePost} deleting={deleting}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}